import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TableContainer } from "@/components/common/TableContainer";
import { DataView } from "@/components/common/DataView";
import { Plus, Upload, Download, RefreshCw } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "@/store/store";
import {
  fetchCustomersRequest,
  searchCustomersRequest,
  exportCustomersRequest,
  importCustomersRequest,
  clearSearchResults,
} from "@/store/slices/customer.slice";
import { columns } from "@/components/customers/columns";
import CustomerFilterModal from "@/components/customers/CustomerFilterModal";

export default function CustomersPage() {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const pageSize = 10;

  const {
    items: customers,
    loading,
    error,
    pagination,
    searchResults,
    searchLoading,
    searchError,
    searchPagination,
  } = useSelector((state: RootState) => state.customers);

  const fetchCustomers = useCallback(() => {
    if (searchQuery.trim()) {
      dispatch(
        searchCustomersRequest({
          q: searchQuery,
          page: pageIndex + 1,
          page_size: pageSize,
        }),
      );
    } else {
      dispatch(
        fetchCustomersRequest({
          page: pageIndex + 1,
          limit: pageSize,
          filters: appliedFilters,
        }),
      );
    }
  }, [dispatch, searchQuery, appliedFilters, pageIndex, pageSize]);

  useEffect(() => {
    const hasData = searchQuery.trim() ? (searchResults?.length || 0) > 0 : (customers?.length || 0) > 0;
    // Only fetch if we don't have data OR if search/filters/page changed
    if (!hasData || pageIndex > 0 || searchQuery.trim() || Object.keys(appliedFilters).length > 0) {
      fetchCustomers();
    }
  }, [searchQuery, appliedFilters, pageIndex, searchResults, customers]);

  useEffect(() => {
    setPageIndex(0);
  }, [searchQuery, appliedFilters]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPageIndex(0);
    if (!query.trim()) {
      dispatch(clearSearchResults());
    }
  };

  const handleFilter = (filters: any) => {
    setAppliedFilters(filters);
    setPageIndex(0);
  };

  const handleRowClick = (customer: any) => {
    const storeType = params?.storeType as string;
    const storeId = params?.storeId as string;
    navigate(`/${storeType}/${storeId}/customers/${customer.id}`);
  };

  const handlePageChange = (newPageIndex: number) => {
    setPageIndex(newPageIndex);
  };

  const handleAddCustomer = () => {
    const storeType = params?.storeType as string;
    const storeId = params?.storeId as string;
    navigate(`/${storeType}/${storeId}/customers/create`);
  };

  const handleImport = (file: File) => {
    dispatch(importCustomersRequest({ file }));
  };

  const handleExport = () => {
    dispatch(exportCustomersRequest());
  };

  const getActiveFilterCount = () => {
    return Object.values(appliedFilters).filter(
      (value) => value !== "" && value !== undefined && value !== null,
    ).length;
  };

  const filterConfig = {
    component: (
      <CustomerFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleFilter}
        currentFilters={appliedFilters}
      />
    ),
    isOpen: isFilterModalOpen,
    onToggle: () => setIsFilterModalOpen(!isFilterModalOpen),
    activeCount: getActiveFilterCount(),
  };

  // Handle refresh - directly dispatch action to force a fresh fetch
  const handleRefresh = () => {
    if (searchQuery.trim()) {
      dispatch(
        searchCustomersRequest({
          q: searchQuery,
          page: pageIndex + 1,
          page_size: pageSize,
        }),
      );
    } else {
      dispatch(
        fetchCustomersRequest({
          page: pageIndex + 1,
          limit: pageSize,
          filters: appliedFilters,
        }),
      );
    }
  };

  const isLoading = searchQuery.trim() ? searchLoading : loading;

  const actionButtons = [
    {
      label: "Refresh",
      icon: <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />,
      onClick: handleRefresh,
      variant: "outline" as const,
      disabled: isLoading,
    },
    {
      label: "Export",
      icon: <Download className="h-4 w-4" />,
      onClick: handleExport,
      variant: "outline" as const,
    },
    {
      label: "Import",
      icon: <Upload className="h-4 w-4" />,
      onClick: handleImport,
      variant: "outline" as const,
    },
    {
      label: "Add Customer",
      icon: <Plus className="h-4 w-4" />,
      onClick: handleAddCustomer,
      variant: "primary" as const,
    },
  ];

  return (
    <div className="space-y-4">
      <TableContainer>
        <div className="bg-secondary-background p-[18px] border rounded-[16px]">
          <DataView
            columns={columns}
            data={searchQuery.trim() ? searchResults : customers}
            pagination={searchQuery.trim() ? searchPagination : pagination}
            pageIndex={pageIndex}
            onPageChange={handlePageChange}
            loading={searchQuery.trim() ? searchLoading : loading}
            error={searchQuery.trim() ? searchError?.message : error?.message}
            title="Customers"
            description="Manage and view all your customers in one place"
            searchPlaceholder="Search customers..."
            searchQuery={searchQuery}
            onSearch={handleSearch}
            filterConfig={filterConfig}
            onRowClick={handleRowClick}
            enableColumnResizing={true}
            enableRowSelection={false}
            emptyStateType="customers"
            loadingMessage="Loading customers..."
            actionButtons={actionButtons}
          />
        </div>
      </TableContainer>
    </div>
  );
}
